// @flow
import React, { useState, useMemo } from 'react'
import dynamic from 'next/dynamic'
import classnames from 'classnames'
import moment from 'moment'

import en from 'date-fns/locale/en-US'
import cs from 'date-fns/locale/cs'

import { Flex, Label, Select } from 'components/common'
import { useI18n } from '~/core/i18n'
import { useDateRangePickerStyles } from './styles'

// $FlowFixMe
import 'react-datepicker/dist/react-datepicker.css'

const QUICK_FILTER_MAP = {
  CUSTOM: 'custom',
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  ACTUAL_WEEK: 'actualWeek',
  PREVIOUS_WEEK: 'previousWeek',
  ACTUAL_MONTH: 'actualMonth',
  PREVIOUS_MONTH: 'previousMonth',
  ACTUAL_YEAR: 'actualYear',
  LAST_TWO_YEARS: 'lastTwoYears'
}

const generateQuickFilterOptions = msg =>
  Object.values(QUICK_FILTER_MAP).map((value: any) => ({ label: msg(`common.datePicker.quickFilter.${value}`), value }))

const getLocale = () => {
  const language = typeof window === 'undefined' ? 'cz' : window.navigator.language
  const [base] = language.split('-')

  switch (base) {
    case 'cs':
      return cs
    case 'en':
      return en
    default:
      return cs
  }
}

const getTimeFromQuickFilter = filter => {
  if (filter === QUICK_FILTER_MAP.TODAY) {
    const base = moment()
    return {
      startTime: base.startOf('day').toISOString(),
      endTime: base.endOf('day').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.YESTERDAY) {
    const base = moment().subtract(1, 'day')
    return {
      startTime: base.startOf('day').toISOString(),
      endTime: base.endOf('day').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.ACTUAL_WEEK) {
    const base = moment()
    return {
      startTime: base.startOf('isoWeek').toISOString(),
      endTime: base.endOf('isoWeek').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.PREVIOUS_WEEK) {
    const base = moment().subtract(1, 'week')
    return {
      startTime: base.startOf('isoWeek').toISOString(),
      endTime: base.endOf('isoWeek').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.ACTUAL_MONTH) {
    const base = moment()
    return {
      startTime: base.startOf('month').toISOString(),
      endTime: base.endOf('month').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.PREVIOUS_MONTH) {
    const base = moment().subtract(1, 'month')
    return {
      startTime: base.startOf('month').toISOString(),
      endTime: base.endOf('month').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.ACTUAL_YEAR) {
    const base = moment()
    return {
      startTime: base.startOf('year').toISOString(),
      endTime: base.endOf('year').toISOString()
    }
  }

  if (filter === QUICK_FILTER_MAP.LAST_TWO_YEARS) {
    const base = moment().subtract(1, 'year')
    return {
      startTime: base.startOf('year').toISOString(),
      endTime: moment()
        .endOf('year')
        .toISOString()
    }
  }

  return {
    startTime: moment()
      .startOf('day')
      .toISOString(),
    endTime: moment()
      .endOf('day')
      .toISOString()
  }
}

const ReactDatepicker = dynamic(() => import('react-datepicker'), {
  ssr: false
})

type DateRangePickerProps = {
  id?: string,
  startTime?: Date | string,
  endTime?: Date | string,
  quickFilter?: string,
  label?: string,
  isDisabled?: boolean,
  isQuickFilterEnabled?: boolean,
  isTimeSelectable?: boolean,
  wrapperProps?: {
    [key: string]: any
  },
  onChange?: ({ startTime?: string, endTime?: string, quickFilter?: string }) => void
}

const DateRangePicker = ({
  id,
  startTime = new Date(),
  endTime = new Date(),
  label,
  quickFilter = 'today',
  isDisabled,
  isQuickFilterEnabled,
  isTimeSelectable,
  wrapperProps = {},
  onChange = () => {}
}: DateRangePickerProps) => {
  const [msg] = useI18n()
  const [isActive, setActive] = useState(false)
  const classes = useDateRangePickerStyles(isTimeSelectable)

  const [startDate, endDate] = [new Date(startTime), new Date(endTime)]
  const timeFormat = isTimeSelectable ? 'd.M.yyyy HH:mm' : 'd.M.yyyy'
  const filterEndTimes = time => moment(time).isAfter(startTime)
  const filterStartTimes = time => moment(time).isBefore(endTime)

  const locale = useMemo(() => getLocale(), [])
  const quickFilterOptions = useMemo(() => generateQuickFilterOptions(msg), [msg])

  const Picker = (
    <Flex
      alignCenter
      className={classnames(classes.dateRangePicker, {
        active: isActive,
        disabled: isDisabled
      })}
    >
      <i className={classnames('icon icon-m', classes.calendarIcon)}>calendar</i>
      <Flex alignCenter id={id}>
        <ReactDatepicker
          className={classes.customDatePicker}
          dateFormat={timeFormat}
          filterTime={filterStartTimes}
          locale={locale}
          maxDate={endDate}
          selected={startDate}
          showTimeSelect={isTimeSelectable}
          timeFormat="HH:mm"
          onCalendarClose={() => setActive(false)}
          onCalendarOpen={() => setActive(true)}
          onChange={date =>
            onChange({
              startTime: isTimeSelectable
                ? moment(date).toISOString()
                : moment(date)
                    .startOf('day')
                    .toISOString()
            })
          }
        />
        <i className={classnames('icon icon-s', classes.arrowIcon)}>arrowright</i>
        <ReactDatepicker
          className={classes.customDatePicker}
          dateFormat={timeFormat}
          filterTime={filterEndTimes}
          locale={locale}
          minDate={startDate}
          selected={endDate}
          showTimeSelect={isTimeSelectable}
          timeFormat="HH:mm"
          onCalendarClose={() => setActive(false)}
          onCalendarOpen={() => setActive(true)}
          onChange={date =>
            onChange({
              endTime: isTimeSelectable
                ? moment(date).toISOString()
                : moment(date)
                    .endOf('day')
                    .toISOString()
            })
          }
        />
      </Flex>
    </Flex>
  )

  return (
    <Flex column {...wrapperProps}>
      {label && <Label id={id && `${label}-label`}>{label}</Label>}
      <Flex spacing={2}>
        {isQuickFilterEnabled && (
          <Select
            options={quickFilterOptions}
            value={quickFilterOptions.find(filter => filter.value === quickFilter) || null}
            wrapperProps={{
              style: {
                width: 150
              }
            }}
            onChange={({ value }) => onChange({ quickFilter: value, ...getTimeFromQuickFilter(value) })}
          />
        )}
        {isQuickFilterEnabled ? (quickFilter === QUICK_FILTER_MAP.CUSTOM ? Picker : null) : Picker}
      </Flex>
    </Flex>
  )
}

export { DateRangePicker }
